import {Fragment, type MouseEvent, type ReactNode, useRef, useState} from 'react'
import {ActionList, CounterLabel, Tooltip} from '@primer/react'
import {AssigneeVisual} from './AssigneeVisual'

import {hovercardAttributesForActor} from '@github-ui/hovercards'
import {ProfileReference} from '@github-ui/profile-reference'
import {AgentTaskSidePanel} from '@github-ui/issue-agent-tasks/AgentTaskSidePanel'
import {useAnalytics, useClickAnalytics} from '@github-ui/use-analytics'
import {
  beginCopilotUiReadiness,
  type CopilotUiReadinessView,
} from '@github-ui/copilot-browser-latency/copilot-ui-readiness'

import styles from './Assignees.module.css'

export type AgentTaskInfo = {
  /** Rendered status icon for the agent's most recent task */
  statusIcon?: ReactNode
  /** Number of tasks for this agent on the issue */
  count: number
  /** URL to navigate to when clicking the agent row */
  taskUrl?: string
  /** ID of the most recent task */
  taskId?: string
  /** Repository ID of the most recent task */
  repoId?: number
}

export type Assignee = {
  login: string
  name?: string | null
  avatarUrl: string
  profileResourcePath?: string | null | undefined
  id: string
  displayName: string
  isCopilot: boolean
  isAgent: boolean
  assignedAgents?: Array<Assignee & {agentTaskInfo?: AgentTaskInfo}>
}
export type AssigneesProps = {
  assignees: Assignee[]
  testId?: string
}

export function Assignees({assignees, testId}: AssigneesProps) {
  const {sendAnalyticsEvent} = useAnalytics()
  const {sendClickAnalyticsEvent} = useClickAnalytics()

  const [selectedTaskInfo, setSelectedTaskInfo] = useState<{
    taskId: string
    repoId: number
    uiReadiness?: CopilotUiReadinessView
  } | null>(null)
  const returnFocusRef = useRef<HTMLElement | null>(null)

  const handleClick = () => {
    sendAnalyticsEvent('analytics.click', 'ISSUE_SIDEBAR_ASSIGNEE_CLICK')
  }

  const handleContextMenu = () => {
    sendAnalyticsEvent('analytics.click', 'ISSUE_SIDEBAR_ASSIGNEE_RIGHT_CLICK')
  }

  return (
    <>
      <ActionList variant={'full'} className={styles.assigneesList} data-icv-visible>
        {[...assignees]
          .sort(sortByLogin)
          .map(({login, name, avatarUrl, profileResourcePath, id, isCopilot, displayName, isAgent, assignedAgents}) => {
            const hasNestedAgents = assignedAgents && assignedAgents.length > 0

            return (
              <Fragment key={id}>
                <ActionList.LinkItem
                  href={profileResourcePath ?? undefined}
                  target="_blank"
                  onClick={handleClick}
                  onContextMenu={handleContextMenu}
                  {...hovercardAttributesForActor(login, {isCopilot, isAgent})}
                >
                  <ActionList.LeadingVisual>
                    <AssigneeVisual login={login} id={id} avatarUrl={avatarUrl} isCopilot={isCopilot} />
                  </ActionList.LeadingVisual>
                  <div data-testid={testId} className={styles.assigneeNameLabel}>
                    <ProfileReference login={displayName} profileName={name} isAgent={isAgent} />
                  </div>
                </ActionList.LinkItem>

                {hasNestedAgents && (
                  <li className={styles.nestedAgentListWrapper}>
                    <ActionList aria-label={`Agents assigned by ${displayName}`}>
                      {assignedAgents.map((agent, agentIndex) => {
                        const isLastAgent = agentIndex === assignedAgents.length - 1
                        const taskInfo = agent.agentTaskInfo
                        const agentHref = taskInfo?.taskUrl ?? agent.profileResourcePath ?? undefined
                        const taskId = taskInfo?.taskId
                        const repoId = taskInfo?.repoId

                        const opensSidePanel = taskId !== undefined && repoId !== undefined
                        const linkItem = (
                          <ActionList.LinkItem
                            key={agent.id}
                            href={agentHref}
                            target="_blank"
                            onClick={
                              opensSidePanel
                                ? (e: MouseEvent) => {
                                    // Let modifier-clicks and middle-clicks open the link in a new tab natively
                                    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
                                    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return
                                    e.preventDefault()
                                    const uiReadiness = beginCopilotUiReadiness({
                                      surface: 'issue_panel',
                                      entryKind: 'embedded',
                                      repositoryScope: 'repository',
                                      expectedTaskId: taskId,
                                    })
                                    handleClick()
                                    sendClickAnalyticsEvent({
                                      category: 'agent_task_side_panel',
                                      action: 'open',
                                      label: 'ref_cta:assignee;ref_loc:issue_sidebar',
                                    })
                                    returnFocusRef.current = e.currentTarget as HTMLElement
                                    setSelectedTaskInfo({
                                      taskId,
                                      repoId,
                                      ...(uiReadiness ? {uiReadiness} : {}),
                                    })
                                  }
                                : handleClick
                            }
                            onContextMenu={handleContextMenu}
                            className={styles.nestedAgentItem}
                            data-testid="nested-agent-item"
                            aria-haspopup={opensSidePanel ? 'dialog' : undefined}
                            aria-expanded={opensSidePanel ? selectedTaskInfo?.taskId === taskId : undefined}
                          >
                            <ActionList.LeadingVisual>
                              <span className={styles.nestedAgentVisual}>
                                <span
                                  className={`${styles.connector} ${isLastAgent ? styles.connectorLast : styles.connectorMiddle}`}
                                  aria-hidden="true"
                                />
                                <AssigneeVisual
                                  login={agent.login}
                                  id={agent.id}
                                  avatarUrl={agent.avatarUrl}
                                  isCopilot={agent.isCopilot}
                                />
                              </span>
                            </ActionList.LeadingVisual>
                            <div className={styles.assigneeNameLabel}>
                              <ProfileReference
                                login={agent.displayName}
                                profileName={agent.name}
                                isAgent={agent.isAgent}
                              />
                            </div>
                            {taskInfo != null && taskInfo.statusIcon != null && (
                              <ActionList.TrailingVisual>
                                <span className={styles.agentTaskStatus}>
                                  {taskInfo.count > 1 && (
                                    <span aria-hidden="true">
                                      <CounterLabel className={styles.agentTaskCount}>{taskInfo.count}</CounterLabel>
                                    </span>
                                  )}
                                  {taskInfo.count > 1 && <span className="sr-only">{taskInfo.count} tasks,</span>}
                                  {taskInfo.statusIcon}
                                </span>
                              </ActionList.TrailingVisual>
                            )}
                          </ActionList.LinkItem>
                        )

                        return taskInfo && taskInfo.count > 0 && taskInfo.taskUrl ? (
                          <Tooltip key={agent.id} text={taskInfo.count > 1 ? 'View latest session' : 'View session'}>
                            {linkItem}
                          </Tooltip>
                        ) : (
                          linkItem
                        )
                      })}
                    </ActionList>
                  </li>
                )}
              </Fragment>
            )
          })}
      </ActionList>

      {selectedTaskInfo && (
        <AgentTaskSidePanel
          uiReadiness={selectedTaskInfo.uiReadiness}
          taskId={selectedTaskInfo.taskId}
          repoId={selectedTaskInfo.repoId}
          open
          onClose={() => setSelectedTaskInfo(null)}
          returnFocusRef={returnFocusRef}
        />
      )}
    </>
  )
}

function sortByLogin(a: Pick<Assignee, 'login'>, b: Pick<Assignee, 'login'>) {
  return a.login === b.login ? 0 : a.login > b.login ? 1 : -1
}
